import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { FolderPlus, Upload, RefreshCw } from "lucide-react";
import { Button, FormField, Input } from "@/design-system/components/ui";
import { foldersApi } from "@/api/folders";
import { FolderTree } from "@/components/dms/FolderTree";
import { FolderContents } from "@/components/dms/FolderContents";
import { DocumentUploadModal } from "@/components/dms/DocumentUploadModal";
import type { DocumentFolderTreeDto } from "@/types/api";
import { useAuth } from "@/hooks/useAuth";
import { useDialogs } from "@/hooks/useDialogs";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";

// Document Access Standard v1.1 §3 — the polymorphic owner contract.
// (ownerType, ownerId) uniquely identifies the document scope; the
// rootName? prop is reserved per §8 but resolved server-side today.
export interface DocumentPanelProps {
  ownerType: string;
  ownerId: string;
  rootName?: string;
  /** Deal Pipeline spec §5.5 — seed the document name filter
   * on mount (used by the Overview "View broker submission summary" deep
   * link to land pre-filtered on the SubmissionSummary doc). */
  initialFilter?: string;
}

/**
 * Document Access Standard v1.1 §8 — the single React component that
 * renders every Documents tab in the platform. Extracted from the Test
 * Broker reference implementation so other entity detail pages can drop
 * it in unchanged.
 */
export function DocumentPanel({ ownerType, ownerId, rootName, initialFilter }: DocumentPanelProps) {
  const qc = useQueryClient();
  const dialogs = useDialogs();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canViewArchived = hasPermission("Documents.ViewArchived");
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [filter, setFilter] = useState(initialFilter ?? "");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  // Bugfix Bundle B — track whether the auto-provision attempt has already
  // run for this (ownerType, ownerId) so a 404 from the ensure endpoint
  // (no template registered) doesn't loop. The ref resets when the panel
  // is re-mounted for a different owner.
  const provisionAttempted = useRef<string | null>(null);

  const treeQuery = useQuery({
    queryKey: ["dms", "entityTree", ownerType, ownerId],
    queryFn: () => foldersApi.entityTree(ownerType, ownerId),
    retry: false,
  });

  const ensureTree = useMutation({
    mutationFn: () => foldersApi.ensureEntityTree(ownerType, ownerId, rootName),
    onSuccess: (tree) => {
      qc.setQueryData(["dms", "entityTree", ownerType, ownerId], tree);
      // Clear the stale error state from the initial 404 so the panel
      // re-renders against the freshly provisioned tree.
      treeQuery.refetch();
    },
  });

  // Document Access Standard v1.1 §5 — auto-provision on first render. If
  // the GET returned 404, fire the ensure POST exactly once per mount so
  // the user sees a populated tree immediately rather than the legacy
  // "tree will appear after first upload" placeholder.
  useEffect(() => {
    const key = `${ownerType}:${ownerId}`;
    if (
      treeQuery.isError &&
      provisionAttempted.current !== key &&
      !ensureTree.isPending
    ) {
      provisionAttempted.current = key;
      ensureTree.mutate();
    }
  }, [treeQuery.isError, ownerType, ownerId, ensureTree]);

  useMemo(() => {
    if (!selectedFolderId && treeQuery.data) {
      setSelectedFolderId(treeQuery.data.id);
    }
  }, [treeQuery.data, selectedFolderId]);

  const createFolder = useMutation({
    mutationFn: (name: string) =>
      foldersApi.create({
        name,
        parentFolderId: selectedFolderId,
        entityType: ownerType,
        entityId: ownerId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dms", "entityTree", ownerType, ownerId] });
      // The shared FolderPicker (staging file-into picker, move modal) lists
      // folders under this prefix — stale-mark it so the new subfolder shows.
      qc.invalidateQueries({ queryKey: ["folder-picker-tree"] });
      toast.success("Folder created.");
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err));
    },
  });

  const handleNewSubfolder = async () => {
    const name = await dialogs.prompt({
      title: "New subfolder",
      label: "Subfolder name",
      placeholder: "e.g. 2026 Q1 Statements",
    });
    if (name && name.trim()) createFolder.mutate(name.trim());
  };

  // Document Access Standard v1.1 §5 — auto-provisioning surface. The
  // useEffect above fires the ensure POST as soon as the GET 404s; while
  // it's in flight we show a transient setup state. If ensure also 404s
  // (no template registered for this ownerType) we surface an actionable
  // admin message rather than the legacy "wait until first upload" copy.
  if (treeQuery.isError && !ensureTree.data) {
    if (ensureTree.isError) {
      return (
        <div className="p-space-4 bg-warm-white rounded-md text-small text-gray-mid">
          Document folders aren't set up for this {ownerType.toLowerCase()} yet.
          An administrator can register a folder template at{" "}
          <span className="font-mono">/admin/folder-templates</span>.
        </div>
      );
    }
    return (
      <div className="p-space-4 bg-warm-white rounded-md text-small text-gray-mid">
        Setting up document folders for this {ownerType.toLowerCase()}…
      </div>
    );
  }

  // Prefer the live query cache; fall back to the freshly provisioned
  // tree from the ensure mutation while the cache refetch is in flight.
  const tree = treeQuery.data ?? ensureTree.data;
  const selectedFolderName = findFolder(tree, selectedFolderId)?.name;

  return (
    <div className="grid grid-cols-12 gap-space-4">
      <aside className="col-span-12 md:col-span-4 lg:col-span-3">
        <div className="flex items-center gap-space-2 mb-space-3">
          <FormField label="Find in folder" htmlFor="entity-tree-filter" className="flex-1">
            <Input
              id="entity-tree-filter"
              placeholder="Search folders"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </FormField>
          <Button
            variant="icon"
            size="icon"
            onClick={() => treeQuery.refetch()}
            aria-label="Refresh folders"
          >
            <RefreshCw size={14} aria-hidden />
          </Button>
        </div>
        {tree ? (
          <FolderTree
            nodes={[tree]}
            selectedId={selectedFolderId}
            onSelect={(n) => setSelectedFolderId(n.id)}
            initiallyExpanded
            filter={filter}
          />
        ) : (
          <div className="text-caption text-gray-mid">Loading folders…</div>
        )}
      </aside>

      <section className="col-span-12 md:col-span-8 lg:col-span-9">
        <div className="flex flex-wrap items-center gap-space-2 mb-space-3">
          <h3 className="font-display font-bold text-h4 text-onyx m-0">
            {selectedFolderName ?? "Select a folder"}
          </h3>
          <div className="ml-auto flex items-center gap-space-2">
            {canViewArchived && (
              <button
                type="button"
                role="switch"
                aria-checked={showArchived}
                onClick={() => setShowArchived((v) => !v)}
                className="inline-flex items-center gap-space-2 text-small text-gray-mid hover:text-onyx"
              >
                <span
                  className={`relative inline-block w-[32px] h-[18px] rounded-full transition-colors ${
                    showArchived ? "bg-gold" : "bg-border-light"
                  }`}
                  aria-hidden
                >
                  <span
                    className={`absolute top-[2px] left-[2px] w-[14px] h-[14px] rounded-full bg-white-pure shadow transition-transform ${
                      showArchived ? "translate-x-[14px]" : ""
                    }`}
                  />
                </span>
                Show archived
              </button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={handleNewSubfolder}
              disabled={!selectedFolderId}
            >
              <FolderPlus size={14} aria-hidden /> New subfolder
            </Button>
            <Button
              size="sm"
              onClick={() => setUploadOpen(true)}
              disabled={!selectedFolderId}
            >
              <Upload size={14} aria-hidden /> Upload
            </Button>
          </div>
        </div>

        {selectedFolderId && (
          <FolderContents
            folderId={selectedFolderId}
            onFolderSelect={(id) => setSelectedFolderId(id)}
            archiveScope={showArchived ? "All" : "ActiveOnly"}
            onShowArchivedRequested={
              canViewArchived && !showArchived
                ? () => setShowArchived(true)
                : undefined
            }
          />
        )}
      </section>

      <DocumentUploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        targetFolderId={selectedFolderId}
        targetFolderName={selectedFolderName}
        onUploaded={() => {
          qc.invalidateQueries({ queryKey: ["dms", "folderDetail", selectedFolderId] });
          qc.invalidateQueries({ queryKey: ["dms", "entityTree", ownerType, ownerId] });
        }}
      />
    </div>
  );
}

function findFolder(
  node: DocumentFolderTreeDto | undefined,
  id: string | null,
): DocumentFolderTreeDto | null {
  if (!node || !id) return null;
  if (node.id === id) return node;
  for (const c of node.children) {
    const m = findFolder(c, id);
    if (m) return m;
  }
  return null;
}
